import type {
  CreateJDFormDTO,
  JDDetailDTO,
  JDKpiDTO,
  UpdateJDFormDTO,
} from "@/modules/jd/domain/contracts";
import type {
  CreateJDLevelFormDTO,
  JDLevelDTO,
  UpdateJDLevelFormDTO,
} from "@/modules/jd/domain/contracts/level";
import type { JDLevelsListResult } from "@/modules/jd/domain/repo/level/list-levels";
import type { JDVersionDTO } from "@/modules/jd/domain/repo/version/list-versions";
import type { JDListResult } from "@/modules/jd/domain/types";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import { fetchLookup, hydrateLookupItem } from "@/shared/lib/utils";

function toFormData(input: Record<string, unknown>): FormData {
  const fd = new FormData();
  Object.entries(input).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    if (v instanceof File) fd.append(k, v);
    else if (Array.isArray(v) || typeof v === "object") {
      fd.append(k, JSON.stringify(v));
    } else fd.append(k, String(v));
  });
  return fd;
}

export const jdApi = {
  async list(query: OffsetPageQueryDTO): Promise<JDListResult> {
    const url = new URL(`${config.apiUrl}/jd`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<JDListResult>(url.toString());
  },
  async get(id: string): Promise<JDDetailDTO> {
    return fetcher.get<JDDetailDTO>(`${config.apiUrl}/jd/${id}`);
  },
  async getByPositionId(positionId: string): Promise<JDDetailDTO> {
    return fetcher.get<JDDetailDTO>(
      `${config.apiUrl}/jd/position/${positionId}`,
    );
  },
  async create(input: CreateJDFormDTO) {
    return fetcher.postForm<{ value: { id: string } }>(
      `${config.apiUrl}/jd`,
      toFormData(input),
    );
  },
  async update(id: string, input: UpdateJDFormDTO) {
    return fetcher.putForm<{ value: { id: string } }>(
      `${config.apiUrl}/jd/${id}`,
      toFormData(input),
    );
  },
  async publish(id: string) {
    return fetcher.post<{ value: { id: string } }>(
      `${config.apiUrl}/jd/${id}/publish`,
    );
  },
  async unpublish(id: string) {
    return fetcher.post<{ value: { id: string } }>(
      `${config.apiUrl}/jd/${id}/unpublish`,
    );
  },
  async archive(id: string) {
    return fetcher.post<{ value: { id: string } }>(
      `${config.apiUrl}/jd/${id}/archive`,
    );
  },
  async duplicate(id: string, data: { title: string; departmentId: string }) {
    return fetcher.post<{ value: { id: string } }>(
      `${config.apiUrl}/jd/${id}/duplicate`,
      data,
    );
  },
  async delete(id: string) {
    return fetcher.delete<{ value: { ok: true } }>(`${config.apiUrl}/jd/${id}`);
  },
  async getKpis(id: string): Promise<{ data: JDKpiDTO[] }> {
    return fetcher.get<{ data: JDKpiDTO[] }>(
      `${config.apiUrl}/jd/${id}/kpis`,
    );
  },
  async addKpi(id: string, kpiId: string): Promise<{ id: string }> {
    return fetcher.post<{ id: string }>(
      `${config.apiUrl}/jd/${id}/kpis`,
      { kpiId },
    );
  },
  async removeKpi(id: string, kpiId: string): Promise<{ ok: boolean }> {
    return fetcher.delete<{ ok: boolean }>(
      `${config.apiUrl}/jd/${id}/kpis/${kpiId}`,
    );
  },
  async getKpisByPositionId(positionId: string): Promise<{ data: JDKpiDTO[] }> {
    return fetcher.get<{ data: JDKpiDTO[] }>(
      `${config.apiUrl}/jd/position/${positionId}/kpis`,
    );
  },
  async getVersions(id: string): Promise<{ data: JDVersionDTO[] }> {
    return fetcher.get<{ data: JDVersionDTO[] }>(
      `${config.apiUrl}/jd/${id}/versions`,
    );
  },
  async getVersion(id: string, versionId: string): Promise<JDDetailDTO> {
    return fetcher.get<JDDetailDTO>(
      `${config.apiUrl}/jd/${id}/versions/${versionId}`,
    );
  },
  async saveVersion(id: string, changeNote?: string) {
    return fetcher.post<{ value: { id: string; versionNumber: number } }>(
      `${config.apiUrl}/jd/${id}/versions`,
      { changeNote },
    );
  },
  async revertToVersion(id: string, versionId: string) {
    return fetcher.post<{ value: { id: string } }>(
      `${config.apiUrl}/jd/${id}/versions/${versionId}/revert`,
    );
  },
};

export const jdLevelsApi = {
  async list(query: OffsetPageQueryDTO): Promise<JDLevelsListResult> {
    const url = new URL(`${config.apiUrl}/jd/levels`);
    url.searchParams.set("limit", String(query.limit ?? 20));
    url.searchParams.set("offset", String(query.offset ?? 0));
    if (query.sort) url.searchParams.set("sort", JSON.stringify(query.sort));
    if (query.filters)
      url.searchParams.set("filters", JSON.stringify(query.filters));
    return fetcher.get<JDLevelsListResult>(url.toString());
  },
  async get(id: string): Promise<JDLevelDTO> {
    return fetcher.get<JDLevelDTO>(`${config.apiUrl}/jd/levels/${id}`);
  },
  async create(input: CreateJDLevelFormDTO) {
    return fetcher.postForm<{ value: JDLevelDTO }>(
      `${config.apiUrl}/jd/levels`,
      toFormData(input),
    );
  },
  async update(id: string, input: UpdateJDLevelFormDTO) {
    return fetcher.patchForm<{ value: JDLevelDTO }>(
      `${config.apiUrl}/jd/levels/${id}`,
      toFormData(input),
    );
  },
  async delete(id: string) {
    return fetcher.delete<{ value: boolean }>(
      `${config.apiUrl}/jd/levels/${id}`,
    );
  },
  async lookup(params: {
    query: string;
    cursor?: string | null;
    pageSize: number;
  }) {
    return fetchLookup(`${config.apiUrl}/jd/levels/lookup`, params);
  },
  async hydrate(id: string) {
    return hydrateLookupItem(`${config.apiUrl}/jd/levels/lookup`, id);
  },
};
